import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useHistory } from '@useHistory/useHistoryCompat';
import { ReAuthoriseNodeConfirmation } from '../../../../connector/components/ReAuthoriseNodeConfirmation';
import { DESTINATION_TYPES } from '../../../../containers/destination/constants/destinationTypes';
import { useReAuthoriseNode } from '../../../../connector/hooks/useReAuthoriseNode';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { SchemaExplorerUIOptions } from '../../interfaces/schemaExplorer';
import { SchemaExplorerFolder } from '../../interfaces/schemaExplorerFolder';
import styles from './SchemaExplorer.module.scss';
import { NodeLogo } from '@hevoio/hevo-ui-toolkit';
import { HdTooltip, HdIconButton, HdRbacButton, HdRbacIconButton } from '../../../../components';
import HdIcon from '../../../../media/components/icon/HdIcon';
import HdTimeAgo from '../../../../date-time/components/time-ago/HdTimeAgo';
import { getFolders, getNextRenderFoldersCount, searchSchema } from '../../utils/schema-explorer-tree.helper';
import SchemaExplorerTree from '../schema-explorer-tree/SchemaExplorerTree';
import SearchArea from '../../../../list/components/search-area/SearchArea';
import { QUERY_DEBOUNCE_TIME } from '../../../../core/constants/constants';
import { HdCoordinatedScroll } from '../../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import useDontMountAtFirst from '../../../../core/hooks/useDontMountAtFirst';

import { SchemaExplorerShimmer } from '../schema-explorer-shimmer/SchemaExplorerShimmer';
import { EntityUIState, EntityUIStateWrapper } from '../../../../core/interfaces/entitiy-ui-state';
import RetryApiAlert from '../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import ScrollToTopButton from '../../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import DestinationsAPI from '../../../../containers/destination/api/DestinationsAPI';
import { RbacPermissions } from '../../../../core/interfaces/user';
import { HdRbacLink } from '../../../../access-control/components/rbac-link/HdRbacLink';

const FOLDER_MIN_HEIGHT = 25;

export interface SchemaExplorerProps {
  source: any;
  uiState: EntityUIStateWrapper;
  canShowViews: boolean;
  schemaExplorerUIOptions: SchemaExplorerUIOptions;
  showingInsideCard: boolean;
  onCreateTableClick: Function;
  getSource: () => void;
  onFolderDelete: (folderName: string) => void;
  className?: string;
}

export default function SchemaExplorer({
  source,
  uiState,
  canShowViews,
  schemaExplorerUIOptions,
  showingInsideCard,
  onCreateTableClick,
  getSource,
  onFolderDelete,
  className = ''
}: SchemaExplorerProps) {
  const [pageSize, setPageSize] = useState<number>(10);
  const [isSearchExpanded, setSearchExpanded] = useState(false);
  const [renderedFolders, setRenderedFolders] = useState<SchemaExplorerFolder[]>([]);
  const folders = useRef<SchemaExplorerFolder[]>([]);
  const filteredFolders = useRef<SchemaExplorerFolder[]>([]);
  const coordinatedScrollRef = useRef<HTMLDivElement>(null);
  const schemaSearchText = useRef('');
  const showTree = source;
  const showError = !source && uiState.state === EntityUIState.ERRORED;
  const showShimmer =
    !source && (uiState.state === EntityUIState.LOADING || uiState.state === EntityUIState.NEW);
  const canReauthorize = !!source?.config?.oauth_token_id;

  const { reAuthorise, reAuthoriseNodeConfirmationData, onReAuthoriseNodeConfirmationClose } =
    useReAuthoriseNode();

  const appendNextPage = () => {
    if (renderedFolders.length < filteredFolders.current.length) {
      const renderedFoldersCopy = [
        ...renderedFolders,
        ...filteredFolders.current.slice(
          renderedFolders.length,
          renderedFolders.length + getNextRenderFoldersCountFn(renderedFolders.length)
        )
      ];

      setRenderedFolders(renderedFoldersCopy);
    }
  };

  const getNextRenderFoldersCountFn = startIndex =>
    getNextRenderFoldersCount(filteredFolders.current, pageSize, startIndex);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: false,
    hasNextPage: renderedFolders.length < filteredFolders.current.length,
    onLoadMore: appendNextPage,
    rootMargin: '0px 0px 300px 0px'
  });

  useEffect(() => {
    rootRef(coordinatedScrollRef.current);
  }, [coordinatedScrollRef.current]);

  useEffect(() => {
    if (source) {
      if (coordinatedScrollRef.current) {
        setPageSize(Math.ceil(coordinatedScrollRef.current.clientHeight / FOLDER_MIN_HEIGHT));
      }

      getFoldersFn();
    }
  }, [source]);

  useDontMountAtFirst(() => {
    if (source) {
      setRenderedFolders(filteredFolders.current.slice(0, pageSize));
    }
  }, [source, pageSize]);

  const getFoldersFn = () => {
    if (source) {
      folders.current = getFolders(folders.current, source.schemas);
    } else {
      folders.current = [];
    }
    filterFolders(false);
  };
  /**
   * Filters the list of folders on search text
   *
   * @param resetVirtualView - true to not retain the collapsed state
   */
  const filterFolders = (resetVirtualView = true) => {
    const retainCollapseState = !resetVirtualView;

    filteredFolders.current = searchSchema(folders.current, schemaSearchText.current);

    folders.current.forEach((folder, index) => {
      const newRef = filteredFolders.current.find(newFolder => newFolder.id === folder.id);
      if (newRef) {
        if (retainCollapseState) {
          newRef.collapsed = folder.collapsed;
        }
        folders.current[index] = newRef;
      }
    });

    let viewEnd: number;
    if (!resetVirtualView) {
      viewEnd = Math.min(filteredFolders.current.length, renderedFolders.length || pageSize);
    } else {
      viewEnd = getNextRenderFoldersCountFn(0);
    }

    setRenderedFolders(filteredFolders.current.slice(0, viewEnd));
  };

  const onSchemaSearch = (value: string) => {
    if (schemaSearchText.current !== value) {
      schemaSearchText.current = value;
      filterFolders();
    }
    // schemaSearchText.current = value;
    // filterFolders();
  };

  const onFolderToggle = (collapsed, folderIndex) => {
    filteredFolders.current[folderIndex].collapsed = collapsed;
    if (collapsed) {
      fillEmptySpace();
    }
  };

  /**
   * Handles the case when the rendered row count is lesser than page size after collapsing a folder
   */
  const fillEmptySpace = () => {
    const renderedRowsCount = filteredFolders.current
      .slice(0, renderedFolders.length)
      .reduce((count, folder) => count + 1 + (!folder.collapsed ? folder.files.length : 0), 0);
    if (renderedRowsCount < pageSize) {
      appendNextPage();
    }
  };

  const onSchemaRefreshClick = () => {
    if (source && source.id) {
      DestinationsAPI.refreshSchema(source.id);
    }
  };

  const dataIdGenerator = useCallback(getDataIdGenerator('schemaExplorer'), []);

  return (
    <>
      {!showShimmer ? (
        <Box
          className={`surface-box ${styles.schemaExplorerWrapper} ${
            showingInsideCard ? 'border-left-0 border-bottom-0' : ''
          } ${className}`}
        >
          {showTree ? (
            <>
              <div className={styles.sourceDetailsContainer}>
                <NodeLogo
                  roundedBorders
                  logoURL={source.dest_type_logo_url}
                  darkModeLogoURL={source.dest_type_dark_mode_logo_url}
                  primaryColor={DESTINATION_TYPES[source.dest_type].primaryColor}
                  darkModePrimaryColor={DESTINATION_TYPES[source.dest_type].darkModePrimaryColor}
                  className='mr-2'
                />

                <div className={styles.sourceDetails}>
                  <div className='text-bold center-flex-row'>
                    <HdTooltip title={source.name} enableOnTextOverflow>
                      <span className='text-ellipsis'>{source.name}</span>
                    </HdTooltip>

                    <HdTooltip
                      title={
                        <DestinationInfoPopup
                          source={source}
                          canReauthorize={canReauthorize}
                          onReauthoriseAccount={() =>
                            reAuthorise({
                              confirmBody: schemaExplorerUIOptions.reauthoriseWarningBody,
                              nodeType: source.dest_type,
                              oAuthTokenId: source.config.oauth_token_id,
                              oAuthUserId: source.oauth_user_id
                            })
                          }
                          dataIdGenerator={dataIdGenerator}
                        />
                      }
                      mode='surface'
                      customStyle={{ maxWidth: 'initial' }}
                      placement='right-start'
                      disableInteractive={false}
                    >
                      <span
                        className={`hevo-icon ${
                          canReauthorize ? 'hevo-settings' : 'hevo-info'
                        } info-icon info-icon-sm`}
                      />
                    </HdTooltip>
                  </div>

                  <div className={`${styles.sourceRefreshTs} mt-1`}>
                    Refreshed:&nbsp;
                    <b>
                      {source.schema_last_updated ? (
                        <HdTimeAgo timestamp={source.schema_last_updated} />
                      ) : (
                        'Not yet'
                      )}
                    </b>
                    <HdIconButton
                      size='small'
                      name='refresh'
                      type='button'
                      onClick={onSchemaRefreshClick}
                      dataId={dataIdGenerator('refresh')}
                    >
                      <HdIcon name='refresh' size={1} />
                    </HdIconButton>
                  </div>
                </div>
              </div>

              <div
                className={`${styles.schemaSearchContainer} ${
                  !schemaExplorerUIOptions.searchAreaBorder ? styles.searchAreaNoBorder : ''
                }`}
              >
                {schemaExplorerUIOptions.searchAreaBorder ? (
                  <>
                    <span
                      className={`text-uppercase text-bold ${
                        isSearchExpanded ? styles.hideLabel : ''
                      }`}
                    >
                      Schema
                    </span>

                    <SearchArea
                      dataId={dataIdGenerator('')}
                      collapsible
                      autofocus
                      defaultExpanded={false}
                      fullWidth
                      onSearch={onSchemaSearch}
                      debounceInterval={QUERY_DEBOUNCE_TIME}
                      placeholder='Search Schema'
                      onCollapsedStateChange={expanded => setSearchExpanded(expanded)}
                    />
                  </>
                ) : (
                  <SearchArea
                    dataId={dataIdGenerator('expanded')}
                    collapsible={false}
                    autofocus
                    defaultExpanded
                    fullWidth
                    hasBorder={false}
                    iconSize={2}
                    onSearch={onSchemaSearch}
                    debounceInterval={QUERY_DEBOUNCE_TIME}
                    placeholder='Search Warehouse fields or tables'
                  />
                )}
              </div>

              {/* No Search result found */}
              {schemaSearchText.current && !filteredFolders.current.length ? (
                <span className={styles.schemaTreeNoResults}>
                  No results found for above search criteria.
                </span>
              ) : null}

              <HdCoordinatedScroll elRef={coordinatedScrollRef.current}>
                <div
                  id={dataIdGenerator('')}
                  className={styles.listWrapper}
                  ref={coordinatedScrollRef}
                >
                  {schemaExplorerUIOptions.allowTableCreation ? (
                    <div className='px-2 pt-1 pb-1'>
                      <HdRbacButton
                        rbacPermission={RbacPermissions.DESTINATION_EDIT}
                        variation='flat'
                        icon='plus'
                        onClick={() => {
                          onCreateTableClick();
                        }}
                        dataId={dataIdGenerator('create-new-table')}
                      >
                        Create New Table
                      </HdRbacButton>
                    </div>
                  ) : null}
                  <SchemaExplorerTree
                    folders={renderedFolders}
                    source={source}
                    onFolderDelete={onFolderDelete}
                    onFolderToggle={onFolderToggle}
                    canShowViews={canShowViews}
                    schemaExplorerUIOptions={schemaExplorerUIOptions}
                  />{' '}
                  <div ref={sentryRef} />
                  <ScrollToTopButton scrollViewportId={dataIdGenerator('')} />
                </div>
              </HdCoordinatedScroll>
            </>
          ) : null}

          {showError ? (
            <div className={styles.pageError}>
              <RetryApiAlert
                error={uiState.error}
                actionHandler={getSource}
                dataId={dataIdGenerator('')}
              />{' '}
            </div>
          ) : null}

          <ReAuthoriseNodeConfirmation
            data={reAuthoriseNodeConfirmationData}
            onClose={onReAuthoriseNodeConfirmationClose}
          />
        </Box>
      ) : (
        <SchemaExplorerShimmer className={`surface-box ${styles.schemaExplorerShimmer}`} />
      )}
    </>
  );
}

function DestinationInfoPopup({ source, canReauthorize, onReauthoriseAccount, dataIdGenerator }) {
  const history = useHistory();

  const onGotoDestClick = () => {
    history.push({
      pathname: `/destination/d/${source.seq_id}/overview`
    });
  };

  return (
    <div className='config-popup'>
      <HdRbacIconButton
        rbacPermission={RbacPermissions.DESTINATION_EDIT}
        sx={{ paddingTop: '0px' }}
        className='config-popup-action'
        onClick={onGotoDestClick}
        dataId={dataIdGenerator('info-edit')}
      >
        <HdIcon name='edit' />
      </HdRbacIconButton>

      <div className='config-container'>
        {Object.keys(source.display_config).map(key => (
          <div className='config' key={key}>
            <span className='text-medium'>{key}</span>: {source.display_config[key]}
          </div>
        ))}
      </div>

      {canReauthorize ? (
        <HdRbacLink
          tag='button'
          className='mt-3'
          onClick={onReauthoriseAccount}
          rbacPermission={RbacPermissions.DESTINATION_EDIT}
          dataId={dataIdGenerator('reauthorise')}
        >
          Reauthorize Account
        </HdRbacLink>
      ) : null}
    </div>
  );
}
